import React, { useEffect, useMemo, useRef, useState } from "react";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { TIMEOUTS, api } from "@/lib/api";
import { humanBytes, policy } from "@/lib/policy";
import { useFileUrl } from "@/lib/fileAccess";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Palette, Upload, RotateCcw, Hash } from "lucide-react";
import { toast } from "sonner";

const FONTS = ["Inter", "Roboto", "Poppins", "System"];
const DIRECTIONS = [
  { value: "135deg", label: "Diagonală ↘" },
  { value: "to right", label: "Orizontal →" },
  { value: "to bottom", label: "Vertical ↓" },
];
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
// Must match the shape the server accepts, separators included.
const gradient = (dir, from, to) => `linear-gradient(${dir}, ${from}, ${to})`;

function ColorField({ id, label, value, onChange }) {
  const valid = !value || HEX.test(value);
  return (
    <div>
      <label htmlFor={id} className="label-tiny block mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} — selector`}
          value={HEX.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 bg-transparent border border-line/20 cursor-pointer p-0.5"
        />
        <div className="flex-1 flex items-center gap-1.5 px-2 bg-surface-2 border border-line/10 focus-within:border-line/30">
          <Hash className="w-3 h-3 text-fg-muted flex-shrink-0" />
          <input
            id={id}
            value={(value || "").replace(/^#/, "")}
            onChange={(e) => onChange("#" + e.target.value.replace(/^#/, ""))}
            placeholder="176CFF"
            className="flex-1 bg-transparent text-sm mono py-2 outline-none"
          />
        </div>
      </div>
      {!valid && <p className="text-[11px] text-destructive mt-1">Trebuie să fie hex, ex. #176CFF</p>}
    </div>
  );
}

export default function BrandingSettings({ workspaceId }) {
  const { ask, confirmProps } = useConfirm();
  const { current, refreshWorkspaces } = useWorkspace();
  const [name, setName] = useState("");
  const [primary, setPrimary] = useState("");
  const [accent, setAccent] = useState("");
  const [font, setFont] = useState("Inter");
  const [bgMode, setBgMode] = useState("solid");
  const [bgSolid, setBgSolid] = useState("");
  const [bgFrom, setBgFrom] = useState("#12DFF3");
  const [bgTo, setBgTo] = useState("#176CFF");
  const [bgDir, setBgDir] = useState("135deg");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [maxLogo, setMaxLogo] = useState(0);

  useEffect(() => {
    policy().then((p) => setMaxLogo(p.BRAND_LOGO_MAX_MB || 0)).catch(() => {});
  }, []);
  const fileRef = useRef(null);

  // Seed from the workspace, and re-seed when it changes underneath us.
  useEffect(() => {
    if (!current) return;
    setName(current.brand_name || "");
    setPrimary(current.brand_primary_color || "");
    setAccent(current.brand_accent_color || "");
    setFont(current.brand_font || "Inter");
    const bg = current.brand_background || "";
    if (bg.startsWith("linear-gradient")) {
      const m = /^linear-gradient\(([^,]+), (#[0-9a-fA-F]{3,6}), (#[0-9a-fA-F]{3,6})\)$/.exec(bg);
      if (m) { setBgMode("gradient"); setBgDir(m[1]); setBgFrom(m[2]); setBgTo(m[3]); }
    } else {
      setBgMode("solid");
      setBgSolid(bg);
    }
  }, [current]);

  const background = bgMode === "gradient" ? gradient(bgDir, bgFrom, bgTo) : bgSolid;
  const invalid = [primary, accent, bgMode === "solid" ? bgSolid : ""].some((v) => v && !HEX.test(v));
  const logoUrl = useFileUrl(current?.brand_logo_file_id || null);

  const previewStyle = useMemo(() => ({
    background: background || undefined,
    fontFamily: font === "System" ? "system-ui, sans-serif" : `'${font}', system-ui, sans-serif`,
  }), [background, font]);

  const save = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      await api.patch(`/workspaces/${workspaceId}/branding`, {
        brand_name: name,
        brand_primary_color: primary,
        brand_accent_color: accent,
        brand_font: font,
        brand_background: background,
      });
      await refreshWorkspaces();
      toast.success("Branding salvat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setSaving(false); }
  };

  const reset = () => ask({
    title: "Revii la branding-ul Qedara?", destructive: true, confirmLabel: "Resetează",
    description: "Logo-ul și numele custom ale workspace-ului dispar pentru toată lumea.",
    testId: "confirm-reset-branding", onConfirm: doReset,
  });

  const doReset = async () => {
    setSaving(true);
    try {
      await api.patch(`/workspaces/${workspaceId}/branding`, { reset: true });
      await refreshWorkspaces();
      toast.success("Branding resetat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut reseta");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/workspaces/${workspaceId}/branding/logo`, fd, { timeout: TIMEOUTS.upload });
      await refreshWorkspaces();
      toast.success("Logo încărcat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Încărcarea a eșuat");
    } finally { setUploading(false); }
  };

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="branding-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Palette className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Branding</h3>
        <span className="label-tiny ml-2">// WORKSPACE</span>
        <button
          type="button"
          data-testid="branding-reset"
          onClick={reset}
          disabled={saving}
          className="ml-auto text-xs flex items-center gap-1.5 px-2.5 py-1 border border-line/20 text-fg/60 hover:text-fg hover:border-line/40 disabled:opacity-50"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Resetează
        </button>
      </div>

      <div className="p-5 grid gap-6 lg:grid-cols-[1fr_260px]">
        <div className="space-y-4">
          <div>
            <label htmlFor="brand-name" className="label-tiny block mb-1.5">Nume afișat</label>
            <input
              id="brand-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Qedara Chat" maxLength={60} className="input-flat w-full"
            />
          </div>

          <div>
            <span className="label-tiny block mb-1.5">Logo</span>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-surface-2 border border-line/10 flex items-center justify-center flex-shrink-0">
                {logoUrl
                  ? <img src={logoUrl} alt="Sigla workspace-ului" className="w-full h-full object-contain" />
                  : <Palette className="w-4 h-4 text-fg-muted" />}
              </div>
              <button
                type="button" data-testid="branding-logo-upload"
                onClick={() => fileRef.current?.click()} disabled={uploading}
                className="btn-ghost text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Se încarcă…" : "Încarcă logo"}
              </button>
              <input
                ref={fileRef} type="file" className="hidden"
                accept="image/jpeg,image/png,image/svg+xml,image/webp" onChange={uploadLogo}
              />
            </div>
            <p className="text-[11px] text-fg-muted mt-1.5" data-testid="logo-limit">
              JPEG, PNG, SVG sau WebP{maxLogo ? `, maximum ${humanBytes(maxLogo)}` : ""}.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <ColorField id="brand-primary" label="Culoare principală" value={primary} onChange={setPrimary} />
            <ColorField id="brand-accent" label="Culoare accent" value={accent} onChange={setAccent} />
          </div>

          <div>
            <label htmlFor="brand-font" className="label-tiny block mb-1.5">Font</label>
            <select
              id="brand-font" value={font} onChange={(e) => setFont(e.target.value)}
              className="input-flat w-full"
            >
              {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          <div>
            <span className="label-tiny block mb-1.5">Fundal</span>
            <div className="flex items-center gap-4 mb-2 text-sm">
              {["solid", "gradient"].map((m) => (
                <label key={m} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio" name="brand-bg-mode" value={m}
                    data-testid={`brand-bg-${m}`}
                    checked={bgMode === m} onChange={() => setBgMode(m)}
                  />
                  <span className="capitalize">{m}</span>
                </label>
              ))}
            </div>
            {bgMode === "solid" ? (
              <ColorField id="brand-bg-solid" label="Culoare fundal" value={bgSolid} onChange={setBgSolid} />
            ) : (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-4">
                  <ColorField id="brand-bg-from" label="De la" value={bgFrom} onChange={setBgFrom} />
                  <ColorField id="brand-bg-to" label="Până la" value={bgTo} onChange={setBgTo} />
                </div>
                <div>
                  <label htmlFor="brand-bg-dir" className="label-tiny block mb-1.5">Direcție</label>
                  <select
                    id="brand-bg-dir" value={bgDir} onChange={(e) => setBgDir(e.target.value)}
                    className="input-flat w-full"
                  >
                    {DIRECTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          <button
            type="button" data-testid="branding-save" onClick={save} disabled={saving || invalid}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {saving ? "Se salvează…" : "Salvează branding"}
          </button>
        </div>

        {/* Live preview — a miniature of the sidebar, painted with the values above. */}
        <div>
          <span className="label-tiny block mb-1.5">// PREVIZUALIZARE</span>
          <div
            data-testid="branding-preview"
            className="border border-line/10 overflow-hidden"
            style={previewStyle}
          >
            <div className="px-3 py-2.5 flex items-center gap-2 border-b border-line/10">
              <div className="w-7 h-7 rounded-full overflow-hidden bg-surface-2 flex items-center justify-center flex-shrink-0">
                {logoUrl
                  ? <img src={logoUrl} alt="" className="w-full h-full object-contain" />
                  : <span className="text-[10px] mono text-fg-muted">Q</span>}
              </div>
              <span className="text-sm font-bold truncate">{name || "Qedara Chat"}</span>
            </div>
            <div className="p-3 space-y-2">
              {["general", "design", "random"].map((c, i) => (
                <div
                  key={c}
                  className="text-xs px-2 py-1.5 flex items-center gap-1.5"
                  style={i === 0 && HEX.test(primary) ? { background: primary, color: "#fff" } : undefined}
                >
                  <span className="opacity-60">#</span>{c}
                </div>
              ))}
              <div className="pt-2 flex gap-1.5">
                <span
                  className="px-2 py-1 text-[10px] mono"
                  style={HEX.test(primary) ? { background: primary, color: "#fff" } : undefined}
                >principală</span>
                <span
                  className="px-2 py-1 text-[10px] mono"
                  style={HEX.test(accent) ? { background: accent, color: "#fff" } : undefined}
                >accent</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog {...confirmProps} />
    </section>
  );
}
